/* =========================================================================
 * ADD A CHAMPIONSHIP OPPORTUNITY.
 *
 * Brian: "we should easily be able to add new championship opportunities."
 *
 * The board can only work the records it already has. The 2026 USEF Para
 * Dressage National Championships is the case that proved it — a governing body
 * SR was not tracking, running a national championship with two crowns in it,
 * and no way to put it into the universe. However good the hunting gets, a
 * championship nobody entered is invisible.
 *
 * DELIBERATELY SHORT. Name, sport and year are required; everything else is
 * optional. This adds an OPPORTUNITY, not a finished record — the hunt fills in
 * the champion, the resolve layer handles splits and corrections. Demanding a
 * complete record up front is how a quick-add becomes something nobody uses.
 *
 * A FUTURE DATE IS ALLOWED HERE, and that is the whole point: this is something
 * to pursue BEFORE it happens. The crown is the thing that may never carry a
 * future date, and the sweep and resolve layers both enforce that separately.
 * ========================================================================= */

import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import { checkNewChampionship, toChampionshipItem, describeCreate } from './newChampionship.js'
import styles from './pursuit.module.css'

const EMPTY = {
  name: '', sport: '', seasonYear: String(new Date().getFullYear()),
  org: '', level: '', gender: '', state: '', segment: '', eventDate: '',
}

export default function AddChampionship({ onAdded }) {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.SYSTEM_RUN)
  const [open, setOpen] = useState(false)
  const [f, setF] = useState(EMPTY)
  const [state, setState] = useState({ status: 'idle' })

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }))
  const problems = checkNewChampionship(f)
  const ready = problems.length === 0
  const preview = ready ? describeCreate(f) : null

  const submit = async (dryRun) => {
    setState({ status: 'saving' })
    try {
      const r = await api.championshipsLoad([toChampionshipItem(f)], { dryRun })
      if (dryRun) { setState({ status: 'plan', r }); return }
      setState({ status: 'done', r })
      if (r?.created > 0) onAdded && onAdded()
    } catch (err) {
      setState({ status: 'error', message: err?.body?.detail || err?.body?.error || err?.message || 'Could not add it.' })
    }
  }

  if (!allowed) {
    return (
      <div className={styles.sweepDenied}>
        Add a championship — {why(ACTIONS.SYSTEM_RUN) || 'available to exec and ops.'}
      </div>
    )
  }

  return (
    <section className={styles.sweep}>
      <button type="button" className={styles.sweepHead} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className={styles.sweepTitle}>Add a championship</span>
        <span className={styles.sweepSub}>
          Put a new opportunity on the board — the hunt fills in the rest
        </span>
        <span className={styles.sweepChevron}>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className={styles.sweepBody}>
          <div className={styles.addGrid}>
            <label className={styles.resolveField}>
              <span>Championship name *</span>
              <input value={f.name} onChange={set('name')}
                placeholder="2026 USEF Para Dressage National Championships" />
            </label>
            <label className={styles.resolveField}>
              <span>Sport *</span>
              <input value={f.sport} onChange={set('sport')} placeholder="Equestrian" />
            </label>
            <label className={styles.resolveField}>
              <span>Season year *</span>
              <input value={f.seasonYear} onChange={set('seasonYear')} placeholder="2026" inputMode="numeric" />
            </label>
            <label className={styles.resolveField}>
              <span>Governing body</span>
              <input value={f.org} onChange={set('org')} placeholder="USEF" />
            </label>
            <label className={styles.resolveField}>
              <span>Level</span>
              <input value={f.level} onChange={set('level')} placeholder="U.S. national" />
            </label>
            <label className={styles.resolveField}>
              <span>Division / segment</span>
              <input value={f.segment} onChange={set('segment')} placeholder="Grand Prix" />
            </label>
            <label className={styles.resolveField}>
              <span>Gender</span>
              <input value={f.gender} onChange={set('gender')} placeholder="Open / Men's / Women's" />
            </label>
            <label className={styles.resolveField}>
              <span>State</span>
              <input value={f.state} onChange={set('state')} placeholder="IL" />
            </label>
            <label className={styles.resolveField}>
              <span>Event date</span>
              <input type="date" value={f.eventDate} onChange={set('eventDate')} />
            </label>
          </div>

          <p className={styles.resolveNote}>
            Only the name, sport and year are needed. This adds it as <strong>To Pursue</strong> —
            press Hunt champion on the row once it has been contested. A hand-entered date is
            recorded as <em>Dated</em>, never Confirmed: Confirmed should mean somebody checked it
            against the governing body, not that somebody typed it.
          </p>

          {problems.length > 0 && f.name.length > 0 && (
            <ul className={styles.resolveProblems}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          )}

          {/* THE KEY IS SHOWN, because it is the dedupe. Somebody who can see it
              can tell why a second attempt was skipped instead of assuming the
              add silently failed. */}
          {preview && (
            <p className={styles.addKey}>
              Dedupe key: <code>{preview.key}</code> — {preview.note}
            </p>
          )}

          <div className={styles.resolveActions}>
            <button type="button" className={styles.resolveDry} disabled={!ready || state.status === 'saving'}
              onClick={() => submit(true)}>
              Check for a duplicate first
            </button>
            <button type="button" className={styles.resolveGo} disabled={!ready || state.status === 'saving'}
              onClick={() => submit(false)}>
              {state.status === 'saving' ? 'Adding…' : 'Add to the board'}
            </button>
          </div>

          {state.status === 'plan' && (
            <div className={styles.resolvePlan}>
              {state.r?.skipped > 0
                ? 'A championship with this key already exists — adding it would be skipped, not duplicated.'
                : 'No existing record with this key. It would be created.'}
            </div>
          )}

          {state.status === 'done' && (
            <div className={styles.resolveDone}>
              {state.r?.created > 0
                ? '✓ Added. Reload the board to see it.'
                : state.r?.skipped > 0
                  ? 'Already on the board — nothing was duplicated.'
                  : 'Nothing was created.'}
              {state.r?.errors?.length > 0 && (
                <ul className={styles.resolveProblems}>{state.r.errors.map((e) => <li key={e}>{e}</li>)}</ul>
              )}
            </div>
          )}

          {state.status === 'error' && <div className={styles.resolveRefused}>{state.message}</div>}
        </div>
      )}
    </section>
  )
}
